import { useCallback, useEffect, useState } from "react";

import useService from "../../../../hooks/useService";
import {
  buildMedicalBreakdownSummary,
  INITIAL_MEDICAL_BREAKDOWN_SUMMARY,
} from "../components/medical-breakdown/helpers/summary";
import { useSettlementStore } from "../store/settlementStore";

import useSettlementPermissions from "./useSettlementPermissions";

export default function useMedicalBreakdownSection() {
  const caseDetail = useSettlementStore((state) => state.caseDetail);
  const refreshVersion = useSettlementStore((state) => state.refreshVersion);
  const providerSettlementRepository = useService("repositories/provider-settlement-repository");
  const { canViewMedicalBreakdown, canEditMedicalBreakdown } = useSettlementPermissions();
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(INITIAL_MEDICAL_BREAKDOWN_SUMMARY);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    if (!caseDetail?.pk || !canViewMedicalBreakdown) {
      setData([]);
      setSummary(INITIAL_MEDICAL_BREAKDOWN_SUMMARY);
      setStatusOptions([]);
      return;
    }

    setIsLoading(true);

    try {
      const [settlementResponse, statusResponse] = await Promise.all([
        providerSettlementRepository.getByCase(caseDetail.pk),
        providerSettlementRepository.getStatusOptions(),
      ]);

      setData(settlementResponse?.results || []);
      setSummary(buildMedicalBreakdownSummary(settlementResponse));
      setStatusOptions(statusResponse || []);
    } finally {
      setIsLoading(false);
    }
  }, [canViewMedicalBreakdown, caseDetail?.pk, providerSettlementRepository]);

  const fetchSummary = useCallback(async () => {
    if (!caseDetail?.pk || !canViewMedicalBreakdown) {
      setSummary(INITIAL_MEDICAL_BREAKDOWN_SUMMARY);
      return;
    }

    try {
      setIsSummaryLoading(true);
      const settlementResponse = await providerSettlementRepository.getByCase(caseDetail.pk);
      setSummary(buildMedicalBreakdownSummary(settlementResponse));
    } finally {
      setIsSummaryLoading(false);
    }
  }, [canViewMedicalBreakdown, caseDetail?.pk, providerSettlementRepository]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  useEffect(() => {
    if (refreshVersion > 0) {
      fetchSummary();
    }
  }, [fetchSummary, refreshVersion]);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setAddOpen(true);
  }, []);

  const handleCloseAddModal = useCallback((value) => {
    if (typeof value === "boolean") {
      setAddOpen(value);
      return;
    }

    setAddOpen(false);
  }, []);

  return {
    addOpen,
    caseDetail,
    data,
    summary,
    isLoading,
    isSummaryLoading,
    statusOptions,
    canViewMedicalBreakdown,
    canEditMedicalBreakdown,
    handleCloseAddModal,
    handleOpenAddModal,
    handleRefresh,
  };
}
